import * as THREE from 'three';
import { damp } from '@/math/angles';
import { forwardX, forwardZ, headingFromForward } from '@/math/orientation';

/**
 * BallArrow (M67, owner) — a ground pointer that appears on the course BEHIND
 * the player's car when their ball has fallen well behind them, aimed at the
 * ball and GROWING the further back it is. It REPLACES the old top-centre
 * compass: glance down-course and a stylized arrow shows where your ball is.
 * When the ball is ahead (or only a hair behind) there's nothing — you can just
 * see it.
 *
 * WORLD-SPACE 3D decal (reads identically on desktop + phone, any orientation),
 * UNLIT so it stays visible in any city. M67b (owner): it's a canvas-textured
 * quad now — a glossy stylized arrow with a soft glow (no hard outline) and a
 * little SOCCER BALL in the middle; it only shows once the ball is > ~3 lane
 * marks behind (so a quick overshoot you're about to reverse out of doesn't
 * flash it), and it starts smaller so it never outgrows the screen.
 */

const OFFSET = 4.5; // how far behind the car the arrow sits (world units)
// White lane dashes cycle ≈ 8 u (e.g. NYC rdash 3-on/5-off). "> 3 marks" ≈ 24 u
// — only appears when the ball is genuinely lost behind you, not on an overshoot.
const BEHIND_ON = 24;
const GROW_RANGE = 50; // ball this much further back than BEHIND_ON = full size
const SIZE_MIN = 0.95; // small when it first appears…
const SIZE_MAX = 1.5; // …and capped so it never outgrows the view even far back
const PLANE = 1.55; // base quad size (world units) before the size multiplier
const TEX = 256; // arrow texture resolution

// M88 (owner): the arrow's brand palette — deep yellow gold shading into a
// deeper, elegant red. How much of the arrow the red claims tracks how far
// back the ball is (see paintArrow's `far`).
const GOLD = 0xf2b21c;
const RED = 0xa61e2e;
/** How many distinct gradient mixes we paint (repaints are quantized to
 *  these, so chasing a runaway ball costs at most 9 tiny canvas repaints). */
const FAR_BUCKETS = 10;

/** color (0xrrggbb) shaded toward white (f>0) or black (f<0). */
function shade(hex: number, f: number): string {
  let r = (hex >> 16) & 255;
  let g = (hex >> 8) & 255;
  let b = hex & 255;
  if (f >= 0) {
    r += (255 - r) * f;
    g += (255 - g) * f;
    b += (255 - b) * f;
  } else {
    const k = 1 + f;
    r *= k;
    g *= k;
    b *= k;
  }
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}

/** Blend two 0xrrggbb colours (k = 0 → a, 1 → b). */
function mixHex(a: number, b: number, k: number): number {
  const ar = (a >> 16) & 255,
    ag = (a >> 8) & 255,
    ab = a & 255;
  const br = (b >> 16) & 255,
    bg = (b >> 8) & 255,
    bb = b & 255;
  return (
    ((ar + (br - ar) * k) << 16) | (((ag + (bg - ag) * k) | 0) << 8) | ((ab + (bb - ab) * k) | 0)
  );
}

/** Trace a small pentagon (soccer-ball panel) into the current path. */
function pentagon(x: CanvasRenderingContext2D, cx: number, cy: number, r: number, rot: number): void {
  x.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = rot + (i * Math.PI * 2) / 5;
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) x.moveTo(px, py);
    else x.lineTo(px, py);
  }
  x.closePath();
}

/**
 * Paint the sleek swept dart (pointing UP = toward the ball) + a soccer ball.
 * `far` (0..1) drives the owner's gradient rule: the body runs deep GOLD at
 * the tip into elegant RED at the tail — at far=0 the red claims only the
 * tail, and the takeover point slides toward the tip as the ball falls
 * further behind, so a distant ball reads mostly red at a glance.
 */
function paintArrow(canvas: HTMLCanvasElement, far: number): void {
  const s = canvas.width;
  const x = canvas.getContext('2d');
  if (!x) return;
  x.clearRect(0, 0, s, s);
  const cx = s / 2;
  const tipY = s * 0.04;
  const baseY = s * 0.9;
  const halfW = s * 0.3; // slimmer than the old dart — sleeker silhouette
  const notch = s * 0.19; // deeper tail notch → a swept modern chevron
  const path = (): void => {
    x.beginPath();
    x.moveTo(cx, tipY);
    // Trailing edges bow gently outward (quadratics) — the "swept" read.
    x.quadraticCurveTo(cx + halfW * 0.52, s * 0.42, cx + halfW, baseY);
    x.quadraticCurveTo(cx + halfW * 0.34, baseY - notch * 0.5, cx, baseY - notch);
    x.quadraticCurveTo(cx - halfW * 0.34, baseY - notch * 0.5, cx - halfW, baseY);
    x.quadraticCurveTo(cx - halfW * 0.52, s * 0.42, cx, tipY);
    x.closePath();
  };
  const glowTint = mixHex(GOLD, RED, far); // the halo follows the dominant hue

  // Soft outer glow (no hard outline).
  x.save();
  x.shadowColor = shade(glowTint, 0.2);
  x.shadowBlur = s * 0.1;
  path();
  x.fillStyle = shade(glowTint, 0);
  x.fill();
  path(); // a second pass deepens the glow
  x.fill();
  x.restore();

  // The gold→red body gradient. `redFrom` is where red takes over — it slides
  // from the tail (near) toward the tip (far), per the owner's spec.
  const redFrom = 0.82 - 0.62 * far;
  const g = x.createLinearGradient(0, tipY, 0, baseY);
  g.addColorStop(0, shade(GOLD, 0.35)); // hot golden tip
  g.addColorStop(Math.max(0.02, redFrom * 0.5), shade(GOLD, 0.02));
  g.addColorStop(Math.min(0.98, redFrom), shade(mixHex(GOLD, RED, 0.55), -0.02));
  g.addColorStop(1, shade(RED, -0.16 - 0.1 * far)); // deeper red the further back
  path();
  x.fillStyle = g;
  x.fill();

  // Top sheen (clipped to the arrow).
  path();
  x.save();
  x.clip();
  const sheen = x.createLinearGradient(0, tipY, 0, s * 0.5);
  sheen.addColorStop(0, 'rgba(255,255,255,0.55)');
  sheen.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = sheen;
  x.fillRect(0, 0, s, s * 0.5);
  x.restore();

  // A soft LIGHT inner rim (never black — the owner asked to drop the outline).
  path();
  x.lineWidth = s * 0.016;
  x.strokeStyle = shade(glowTint, 0.62);
  x.stroke();

  // The little soccer ball in the middle of the arrow body.
  const bcx = cx;
  const bcy = s * 0.58;
  const br = s * 0.12;
  x.beginPath();
  x.arc(bcx, bcy, br, 0, Math.PI * 2);
  x.fillStyle = '#f3f5f8';
  x.fill();
  x.lineWidth = br * 0.14;
  x.strokeStyle = 'rgba(18,22,28,0.5)';
  x.stroke();
  // panels: a central pentagon + five clipped edge patches
  x.save();
  x.beginPath();
  x.arc(bcx, bcy, br, 0, Math.PI * 2);
  x.clip();
  x.fillStyle = '#171b22';
  pentagon(x, bcx, bcy, br * 0.44, -Math.PI / 2);
  x.fill();
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
    pentagon(x, bcx + Math.cos(a) * br * 0.92, bcy + Math.sin(a) * br * 0.92, br * 0.34, a);
    x.fill();
  }
  x.restore();
}

export class BallArrow {
  readonly group = new THREE.Group();
  private mat: THREE.MeshBasicMaterial;
  private canvas: HTMLCanvasElement;
  private tex: THREE.CanvasTexture;
  /** Last painted gradient bucket (M88 — quantized `far`, see FAR_BUCKETS). */
  private paintedBucket = -1;
  private shown = 0; // eased visibility 0..1
  private pulse = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = TEX;
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 4; // stays crisp at the grazing chase-cam angle
    // depthTest:false so the flat decal always draws ON TOP of the (cambered)
    // road behind the car; it sits just behind the car so it hides nothing real.
    this.mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const geo = new THREE.PlaneGeometry(PLANE, PLANE);
    geo.rotateX(Math.PI / 2); // lie flat; the texture's top (arrow tip) → +Z (aim dir)
    this.group.add(new THREE.Mesh(geo, this.mat));
    this.group.renderOrder = 4;
    this.group.visible = false;
    this.setFar(0);
  }

  /** Repaint for a new distance bucket (0 = just behind → mostly gold;
   *  1 = far back → mostly red). Quantized so per-frame calls are ~free. */
  private setFar(far: number): void {
    const bucket = Math.round(Math.min(1, Math.max(0, far)) * (FAR_BUCKETS - 1));
    if (bucket === this.paintedBucket) return;
    this.paintedBucket = bucket;
    paintArrow(this.canvas, bucket / (FAR_BUCKETS - 1));
    this.tex.needsUpdate = true;
  }

  /** Drive the arrow from the player's interpolated car + ball. `groundAt`
   *  returns the track height at a world (x,z); pass the reduced-motion flag to
   *  drop the pulse. */
  update(
    carX: number,
    carZ: number,
    carHeading: number,
    ballX: number,
    ballZ: number,
    groundAt: (x: number, z: number) => number,
    dt: number,
    reducedMotion: boolean,
  ): void {
    const dx = ballX - carX;
    const dz = ballZ - carZ;
    const fwd = dx * forwardX(carHeading) + dz * forwardZ(carHeading);
    const behindDist = -fwd; // >0 when the ball is behind the car
    const target = behindDist > BEHIND_ON ? 1 : 0;
    this.shown = damp(this.shown, target, 12, dt);
    if (this.shown < 0.01 && target === 0) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;

    // Sit on the road just behind the car, aimed at the ball.
    const ax = carX - forwardX(carHeading) * OFFSET;
    const az = carZ - forwardZ(carHeading) * OFFSET;
    this.group.position.set(ax, groundAt(ax, az) + 0.09, az);
    this.group.rotation.y = headingFromForward(dx, dz);

    // Grow with how far back the ball is, + a gentle attention pulse. The
    // same distance drives the gold→red gradient mix (M88).
    const grow = Math.min(1, Math.max(0, (behindDist - BEHIND_ON) / GROW_RANGE));
    this.setFar(grow);
    this.pulse += dt;
    const pulseK = reducedMotion ? 1 : 1 + Math.sin(this.pulse * 4.2) * 0.06;
    const size = (SIZE_MIN + grow * (SIZE_MAX - SIZE_MIN)) * this.shown * pulseK;
    this.group.scale.set(size, size, size);
    this.mat.opacity = this.shown;
  }

  /** Ease the arrow out (called when not racing / ball ahead). */
  hide(dt: number): void {
    if (!this.group.visible) return;
    this.shown = damp(this.shown, 0, 12, dt);
    if (this.shown < 0.01) {
      this.group.visible = false;
      return;
    }
    this.mat.opacity = this.shown;
  }

  dispose(): void {
    this.group.children.forEach((c) => (c as THREE.Mesh).geometry.dispose());
    this.tex.dispose();
    this.mat.dispose();
  }
}
